import TrackClass from 'core/logger/track';
import { RootState } from 'src/_common/models/redux/store';
import { Middleware } from 'redux';
import * as commonActionNames from '@Common/models/redux/actions/names';
import { CHAR_LIMITS } from 'config/constants';
import { getOutputKey } from 'src/summarizer/handlers/logic';
import { getTimeDifference } from 'src/_common/handlers/helper';
import { OutputAbs } from 'src/summarizer/models/store';
import {
  TransformedSummarizationResult,
  TransformedSummarizationResultInAbs,
} from 'src/summarizer/models/transformation';
import { GUID } from 'core/utils';
import {
  USER_CHANGED_INPUT_TEXT,
  SYSTEM_FINISHED_SUMMARIZATION,
  FEEDBACK_OPENED,
  SYSTEM_STARTED_SUMMARIZATION,
  USER_CLICKED_COPY_ALL,
  USER_CHANGED_LENGTH_GAUGE,
  USER_CLICKED_INSERT_TEXT,
  CHAR_LIMIT_EXCEEDED,
  USER_UPLOADED_FILE,
  USER_CLICKED_PARAPHRASE_SUMMARY,
  USER_CHANGED_MODE,
  SYSTEM_ERROR_SUMMARIZATION,
  USER_CLICKED_KEYWORD,
  LOADING_KEYWORDS,
  SYSTEM_SET_KEYWORDS,
  USER_PASTED_TEXT,
  HYDRATE_STORE_FROM_SESSION,
} from '../../models/actions/names';
import { ActionTypes } from '../../models/actions';

export const trackSummarizerActions: Middleware = (store) => (next) => (action: ActionTypes) => {
  // add serverside actions in ignoreActions, else it will create window undefined error
  const ignoreActions = [
    USER_CHANGED_INPUT_TEXT,
    // SYSTEM_FINISHED_SUMMARIZATION,
    FEEDBACK_OPENED,
    'PARAMODEL_UPDATE_TOGGLE',
    commonActionNames.SET_LANGUAGE,
    commonActionNames.SET_PLATFORM,
    '__NEXT_REDUX_WRAPPER_HYDRATE__',
    USER_UPLOADED_FILE,
    SYSTEM_SET_KEYWORDS,
    LOADING_KEYWORDS,
    HYDRATE_STORE_FROM_SESSION,
  ];
  const state: RootState = store.getState();
  let wordLimit: number;
  if (state.user?.premium) {
    wordLimit = CHAR_LIMITS.SUMMARIZER_WORD_LIMIT_PREMIUM;
  } else if (state.user?.isAuthenticated) {
    wordLimit = CHAR_LIMITS.SUMMARIZER_WORD_LIMIT_MEMBER;
  } else {
    wordLimit = CHAR_LIMITS.SUMMARIZER_WORD_LIMIT_GUEST;
  }

  if (action.type === CHAR_LIMIT_EXCEEDED && !action.payload.charLimitExceded) {
    ignoreActions.push(CHAR_LIMIT_EXCEEDED);
  }
  if (ignoreActions.indexOf(action.type) !== -1 || !action.type.startsWith('smzr')) {
    next(action);
    return;
  }
  let data: Record<string, unknown> | Record<string, Record<string, unknown>> = { ...action };
  const trackData = action.payload;

  delete data.type;
  let ampData: Record<string, unknown> = {};
  const summaryMode = state.smzr.controls.summaryMode;
  const inputLength = state.smzr.input.inputPara.length;
  const outputKey = getOutputKey(summaryMode, state.smzr.controls.absLengthGauge);

  const setABMetricsData = () => {
    if (summaryMode === 'abs') {
      data.absLengthGauge = state.smzr.controls.absLengthGauge;
      data.modelId = (state.smzr.output[outputKey] as OutputAbs).modelId;
      data.thinkingTime =
        (state.smzr.output[outputKey] as OutputAbs).totalThinkingTime +
        getTimeDifference((state.smzr.output[outputKey] as OutputAbs).thinkingTimeStart);
      data.contentEdited = !(
        (state.smzr.output[outputKey] as OutputAbs).summaryPara === data.modified
      );
      data.numberOfOutputSentences = (state.smzr.output[outputKey] as OutputAbs).numberOfSentences;

      ampData = {
        ...ampData,
        absLengthGauge: data.absLengthGauge,
        modelId: data.modelId,
        thinkingTime: data.thinkingTime,
        triggeredBy: data.triggeredBy,
        contentEdited: data.contentEdited,
        numberOfInputSentences: data.numberOfInputSentences,
      };
    } else if (summaryMode === 'ext') {
      data.extLengthGauge = state.smzr.controls.extLengthGauge;
      data.modelId = state.smzr.output?.ext?.modelId;
      data.thinkingTime =
        state.smzr.output.ext.totalThinkingTime +
        getTimeDifference(state.smzr.output.ext.thinkingTimeStart);
      data.userChangedSliderCount = state.smzr.output.ext.userChangedSliderCount;
      data.modelMaxScore = state.smzr.output.ext.sortedScores[0];
      data.modelMinScore =
        state.smzr.output.ext.sortedScores[state.smzr.output.ext.sortedScores.length - 1];

      data.modelAvgScore = (() => {
        let avg = 0;
        state.smzr.output.ext.scores.forEach((s) => {
          avg += s;
        });
        return avg / state.smzr.output.ext.scores.length;
      })();

      data.userSelectedMinScore =
        state.smzr.output.ext.sortedScores[state.smzr.output.ext.numberOfSentences - 1];
      data.userSelectedMaxScore = state.smzr.output.ext.sortedScores[0];
      data.numberOfOutputSentences = state.smzr.output.ext.numberOfSentences;

      ampData = {
        ...ampData,
        extLengthGauge: data.extLengthGauge,
        modelId: data.modelId,
        thinkingTime: data.thinkingTime,
        userChangedSliderCount: data.userChangedSliderCount,
        triggeredBy: data.triggeredBy,
        numberOfInputSentences: data.numberOfInputSentences,
      };
    }
  };

  switch (action.type) {
    case SYSTEM_STARTED_SUMMARIZATION: {
      let submissionID = state.smzr.controls.submissionID;
      if (state.smzr.input.inputPara !== state.smzr.output.origText) {
        submissionID = GUID();
        action.payload.submissionID = submissionID;
      }
      const lengthData: Record<string, number> = {};
      let keywordData: any = {};
      let keywordCount: any = {};
      if (action.payload.summaryMode === 'abs') {
        lengthData.absLengthGauge = state.smzr.controls.absLengthGauge;
      } else if (action.payload.summaryMode === 'ext') {
        lengthData.extLengthGauge = state.smzr.controls.extLengthGauge;
      }
      if (action.payload.summaryMode === 'abs' && state.smzr.controls.keywords.length) {
        keywordData = {
          shownKeywords: state.smzr.controls.keywords,
          selectedKeywords: state.smzr.controls.selectedKeywords,
        };
        keywordCount = {
          shownKeywordsCount: state.smzr.controls.keywords.length,
          selectedKeywordsCount: state.smzr.controls.selectedKeywords.length,
        };
      }
      ampData = { ...action.payload, ...lengthData, ...keywordCount, submissionID };
      data = { ...action.payload, ...lengthData, ...keywordData, submissionID };
      if (Object.prototype.hasOwnProperty.call(ampData, 'inputPara')) {
        delete ampData.inputPara;
      }
      break;
    }

    case USER_CLICKED_COPY_ALL:
      data = {
        original: state.smzr.output.origText,
        modified: action.payload.copiedText,
        summaryMode,
        triggeredBy: action.payload.triggeredBy,
        numberOfInputSentences: state.smzr.output.origNumberOfSentences,
        submissionID: state.smzr.controls.submissionID,
      };
      ampData = { summaryMode, submissionID: state.smzr.controls.submissionID };
      setABMetricsData();
      break;
    case USER_CLICKED_INSERT_TEXT:
      data = {
        original: state.smzr.output.origText,
        modified: action.payload.insertedText,
        summaryMode,
      };
      ampData = { summaryMode };
      if (summaryMode === 'abs') {
        data.absLengthGauge = state.smzr.controls.absLengthGauge;
        ampData.absLengthGauge = data.absLengthGauge;
      } else if (summaryMode === 'ext') {
        data.extLengthGauge = state.smzr.controls.extLengthGauge;
        ampData.extLengthGauge = data.extLengthGauge;
      }
      break;

    case USER_CHANGED_LENGTH_GAUGE:
      data = {
        ...(trackData as Record<string, Record<string, unknown>>),
        submissionID: state.smzr.controls.submissionID,
      };
      ampData = {
        ...(trackData as Record<string, Record<string, unknown>>),
        submissionID: state.smzr.controls.submissionID,
      };
      if (action.payload.summaryMode === 'ext') {
        delete data.absCurrentValue;
        delete ampData.absCurrentValue;
      } else if (action.payload.summaryMode === 'abs') {
        delete data.summarySentenceSequence;
      }
      delete ampData.summarySentenceSequence;
      break;

    case CHAR_LIMIT_EXCEEDED:
      ampData = {
        ...action.payload,
        wordLimit,
        inputLength,
        wordCount: state.smzr.input.numberOfWords,
      };
      data = {
        ...action.payload,
        wordLimit,
        inputLength,
        wordCount: state.smzr.input.numberOfWords,
      };
      delete ampData.charLimitExceded;
      delete data.charLimitExceded;
      break;

    case USER_CLICKED_PARAPHRASE_SUMMARY:
      data = {};
      // ampData = {};
      break;
    case SYSTEM_FINISHED_SUMMARIZATION:
      data = { summaryMode };
      if (summaryMode === 'abs') {
        data.absLengthGauge = state.smzr.controls.absLengthGauge;
      } else if (summaryMode === 'ext') {
        data.extLengthGauge = state.smzr.controls.extLengthGauge;
      }
      data.modelId = action.payload.modelId;
      data.numberOfInputSentences = state.smzr.output.origNumberOfSentences;
      data.numberOfInputChars = state.smzr.input.numberOfChars;
      data.numberOfInputWords = state.smzr.input.numberOfWords;
      if (summaryMode === 'abs') {
        data.numberOfOutputSentences = (<TransformedSummarizationResultInAbs>(
          action.payload
        )).numberOfSentences;
      } else {
        data.numberOfOutputSentences = (<TransformedSummarizationResult>(
          action.payload
        )).summarySentenceSequence.length;
      }

      data.submissionID = state.smzr.controls.submissionID;
      ampData = { ...data, submissionID: state.smzr.controls.submissionID };
      break;

    case USER_CHANGED_MODE:
      data = {
        ...(trackData as Record<string, Record<string, unknown>>),
        submissionID: state.smzr.controls.submissionID,
      };
      ampData = {
        ...(trackData as Record<string, Record<string, unknown>>),
        submissionID: state.smzr.controls.submissionID,
      };
      delete data.absLengthGauge;
      delete ampData.absLengthGauge;
      break;

    case SYSTEM_ERROR_SUMMARIZATION:
      ampData = {};
      break;
    case USER_CLICKED_KEYWORD:
      data.keyword = trackData;
      if (state.smzr.controls.selectedKeywords.includes(data.keyword as string)) {
        data.status = 'Unselected';
      } else data.status = 'Selected';
      delete data.payload;
      break;

    case USER_PASTED_TEXT:
      data = { ...action.payload };
      ampData = { ...action.payload };
      break;

    default:
      data = {
        ...(trackData as Record<string, Record<string, unknown>>),
        submissionID: state.smzr.controls.submissionID,
      };
      ampData = {
        ...(trackData as Record<string, Record<string, unknown>>),
        submissionID: state.smzr.controls.submissionID,
      };
  }

  if (process.browser) {
    const Track = TrackClass.getInstance();

    Track.storeActionEvent(action.type, data);
    Track.ampEvent(action.type, ampData);
  }

  next(action);
};
